import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  useTheme,
  alpha,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Subscriptions,
  MonetizationOn,
  Analytics,
  Refresh,
  Download,
  Visibility,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import adminService from '../services/adminService';

interface RevenueStats {
  totalRevenue: number;
  monthlyRevenue: number;
  yearlyRevenue: number;
  activeSubscriptions: number;
  totalSubscriptions: number;
  conversionRate: number;
  averageRevenuePerUser: number;
  revenueGrowth: number;
}

interface SubscriptionData {
  _id: string;
  student: {
    name: string;
    email: string;
    grade: number;
  };
  plan: string;
  status: string;
  amount: number;
  startDate: string;
  endDate: string;
  razorpayPaymentId: string;
}

interface PaymentData {
  _id: string;
  studentName: string;
  amount: number;
  currency: string;
  status: string;
  plan: string;
  paymentDate: string;
  razorpayPaymentId: string;
}

const AdminRevenue: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [revenueStats, setRevenueStats] = useState<RevenueStats | null>(null);
  const [subscriptions, setSubscriptions] = useState<SubscriptionData[]>([]);
  const [payments, setPayments] = useState<PaymentData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionData | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  // Pagination state for subscriptions
  const [subscriptionsPage, setSubscriptionsPage] = useState(0);
  const [subscriptionsRowsPerPage, setSubscriptionsRowsPerPage] = useState(10);
  const [subscriptionsTotalCount, setSubscriptionsTotalCount] = useState(0);

  // Pagination state for payments
  const [paymentsPage, setPaymentsPage] = useState(0);
  const [paymentsRowsPerPage, setPaymentsRowsPerPage] = useState(10);
  const [paymentsTotalCount, setPaymentsTotalCount] = useState(0);

  const fetchRevenueData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch revenue analytics
      const revenueResponse = await adminService.getRevenueAnalytics();
      setRevenueStats(revenueResponse.data || {});
      
      // Fetch subscriptions and payments with pagination
      await fetchSubscriptions();
      await fetchPayments();
      
    } catch (err: any) {
      setError(err.message || 'Failed to fetch revenue data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const subscriptionsResponse = await adminService.getSubscriptions({
        page: subscriptionsPage + 1, // Backend uses 1-based pagination
        limit: subscriptionsRowsPerPage,
      });
      setSubscriptions(subscriptionsResponse.data || []);
      setSubscriptionsTotalCount(subscriptionsResponse.total || 0);
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
    }
  };

  const fetchPayments = async () => {
    try {
      const paymentsResponse = await adminService.getPayments({
        page: paymentsPage + 1, // Backend uses 1-based pagination
        limit: paymentsRowsPerPage,
      });
      setPayments(paymentsResponse.data || []);
      setPaymentsTotalCount(paymentsResponse.total || 0);
    } catch (err) {
      console.error('Error fetching payments:', err);
    }
  };

  useEffect(() => {
    fetchRevenueData();
  }, []);

  // Refetch subscriptions when pagination changes (skip initial load)
  useEffect(() => {
    if (revenueStats) { // Only fetch if initial data is loaded
      fetchSubscriptions();
    }
  }, [subscriptionsPage, subscriptionsRowsPerPage]);

  // Refetch payments when pagination changes (skip initial load)
  useEffect(() => {
    if (revenueStats) { // Only fetch if initial data is loaded
      fetchPayments();
    }
  }, [paymentsPage, paymentsRowsPerPage]);

  const formatCurrency = (amount: number) => {
    // Convert paise to rupees (divide by 100)
    const rupees = amount / 100;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(rupees);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return theme.palette.success.main;
      case 'expired':
        return theme.palette.error.main;
      case 'cancelled':
        return theme.palette.warning.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'captured':
      case 'active':
      case 'success':
        return theme.palette.success.main;
      case 'failed':
      case 'error':
        return theme.palette.error.main;
      case 'pending':
      case 'processing':
        return theme.palette.warning.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const handleViewDetails = (subscription: SubscriptionData) => {
    setSelectedSubscription(subscription);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedSubscription(null);
  };

  // Pagination handlers for subscriptions
  const handleSubscriptionsPageChange = (event: unknown, newPage: number) => {
    setSubscriptionsPage(newPage);
  };

  const handleSubscriptionsRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSubscriptionsRowsPerPage(parseInt(event.target.value, 10));
    setSubscriptionsPage(0);
  };

  // Pagination handlers for payments
  const handlePaymentsPageChange = (event: unknown, newPage: number) => {
    setPaymentsPage(newPage);
  };

  const handlePaymentsRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentsRowsPerPage(parseInt(event.target.value, 10));
    setPaymentsPage(0);
  };

  const MetricCard = ({ title, value, change, icon, color, subtitle }: any) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Card
        sx={{
          p: 3,
          height: '100%',
          minHeight: 160,
          background: `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.05)} 100%)`,
          border: `1px solid ${alpha(color, 0.2)}`,
          borderRadius: 2,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 20px ${alpha(color, 0.15)}`,
          },
          transition: 'all 0.3s ease',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h3" sx={{ fontWeight: 'bold', color, mb: 1 }}>
              {value}
            </Typography>
            <Typography variant="h6" color="text.primary" sx={{ mb: 0.5 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ color, opacity: 0.7, fontSize: 40 }}>
            {icon}
          </Box>
        </Box>
        {change !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {change > 0 ? (
              <TrendingUp sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
            ) : change < 0 ? (
              <TrendingDown sx={{ fontSize: 16, color: 'error.main', mr: 0.5 }} />
            ) : null}
            <Typography
              variant="body2"
              sx={{
                color: change > 0 ? 'success.main' : change < 0 ? 'error.main' : 'text.secondary',
                fontWeight: 500,
              }}
            >
              {change > 0 ? '+' : ''}{change}% this month
            </Typography>
          </Box>
        )}
      </Card>
    </motion.div>
  );

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
          Loading revenue data...
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Button variant="contained" onClick={fetchRevenueData} startIcon={<Refresh />}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, py: 1 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            Revenue Analytics
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Financial overview, subscriptions, and payment analytics
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchRevenueData}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            sx={{ bgcolor: theme.palette.success.main }}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Revenue Metrics */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 3, mb: 4 }}>
        <MetricCard
          title="Total Revenue"
          value={formatCurrency(revenueStats?.totalRevenue || 0)}
          change={revenueStats?.revenueGrowth || 0}
          icon={<MonetizationOn />}
          color={theme.palette.success.main}
          subtitle="All time earnings"
        />
        <MetricCard
          title="Monthly Revenue"
          value={formatCurrency(revenueStats?.monthlyRevenue || 0)}
          icon={<TrendingUp />}
          color={theme.palette.primary.main}
          subtitle="Current month"
        />
        <MetricCard
          title="Active Subscriptions"
          value={revenueStats?.activeSubscriptions || 0}
          icon={<Subscriptions />}
          color={theme.palette.secondary.main}
          subtitle={`${revenueStats?.totalSubscriptions || 0} total`}
        />
        <MetricCard
          title="Avg Revenue/User"
          value={formatCurrency(revenueStats?.averageRevenuePerUser || 0)}
          icon={<Analytics />}
          color={theme.palette.warning.main}
          subtitle={`${revenueStats?.conversionRate || 0}% conversion`}
        />
      </Box>

      {/* Subscriptions Table */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Active Subscriptions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subscriptions?.length || 0} subscriptions
            </Typography>
          </Box>
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Student</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Plan</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Start Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>End Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(subscriptions && subscriptions.length > 0) ? (
                  subscriptions.map((subscription) => (
                    <TableRow key={subscription._id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {subscription.student.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Grade {subscription.student.grade} • {subscription.student.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={subscription.plan.toUpperCase()}
                        size="small"
                        sx={{
                          bgcolor: subscription.plan === 'yearly' ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.primary.main, 0.1),
                          color: subscription.plan === 'yearly' ? theme.palette.success.main : theme.palette.primary.main,
                          fontWeight: 'bold',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatCurrency(subscription.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={subscription.status}
                        size="small"
                        sx={{
                          bgcolor: alpha(getStatusColor(subscription.status), 0.1),
                          color: getStatusColor(subscription.status),
                          fontWeight: 'bold',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(subscription.startDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(subscription.endDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleViewDetails(subscription)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No subscriptions found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={subscriptionsTotalCount}
            page={subscriptionsPage}
            onPageChange={handleSubscriptionsPageChange}
            rowsPerPage={subscriptionsRowsPerPage}
            onRowsPerPageChange={handleSubscriptionsRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
            }
          />
        </CardContent>
      </Card>

      {/* Recent Payments */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Recent Payments
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {payments?.length || 0} transactions
            </Typography>
          </Box>
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Student</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Plan</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Payment ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(payments && payments.length > 0) ? (
                  payments.map((payment) => (
                  <TableRow key={payment._id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {payment.studentName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                        {formatCurrency(payment.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={payment.plan.toUpperCase()}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={payment.status}
                        size="small"
                        sx={{
                          bgcolor: alpha(getPaymentStatusColor(payment.status), 0.1),
                          color: getPaymentStatusColor(payment.status),
                          fontWeight: 'bold',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(payment.paymentDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {payment.razorpayPaymentId}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No payments found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={paymentsTotalCount}
            page={paymentsPage}
            onPageChange={handlePaymentsPageChange}
            rowsPerPage={paymentsRowsPerPage}
            onRowsPerPageChange={handlePaymentsRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
            }
          />
        </CardContent>
      </Card>

            {/* Subscription Details Dialog */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={handleCloseDetails}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Subscription Details
        </DialogTitle>
        <DialogContent>
          {selectedSubscription && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Student Information
                </Typography>
                <Typography variant="body2" color="text.secondary">Name</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {selectedSubscription.student.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">Email</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {selectedSubscription.student.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">Grade</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Grade {selectedSubscription.student.grade}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Subscription Details
                </Typography>
                <Typography variant="body2" color="text.secondary">Plan</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {selectedSubscription.plan.toUpperCase()}
                </Typography>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {selectedSubscription.status}
                </Typography>
                <Typography variant="body2" color="text.secondary">Amount</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main, mb: 1 }}>
                  {formatCurrency(selectedSubscription.amount)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Timeline & Payment
                </Typography>
                <Typography variant="body2" color="text.secondary">Start Date</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {formatDate(selectedSubscription.startDate)}
                </Typography>
                <Typography variant="body2" color="text.secondary">End Date</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {formatDate(selectedSubscription.endDate)}
                </Typography>
                <Typography variant="body2" color="text.secondary">Payment ID</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', mb: 1 }}>
                  {selectedSubscription.razorpayPaymentId || 'N/A'}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminRevenue; 